import { useState } from "react";
import { Crown, Lock, CheckCircle2, Smartphone, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCheckout } from "@/lib/useCheckout";
import { FlowerPink, FlowerRed, FlowerBlue, FlowerYellow, FlowerLavender } from "@/components/FlowerDeco";

const perks = [
  "Early access to new scents & seasonal drops",
  "Members-only promotions and quiet sales",
  "Complimentary hand-poured candle on qualifying orders over $100",
  "Behind-the-pour studio notes from Chatoria",
];

export default function MembershipGateway() {
  const [email, setEmail] = useState("");
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const { buy, loadingId } = useCheckout();
  const loading = loadingId === "membership";

  const handleSubscribe = async () => {
    await buy(null, "membership", {
      kind: "membership",
      loadingKey: "membership",
      customer_email: email && email.includes("@") ? email : undefined,
    });
  };

  const handleWaitlist = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email to join the waitlist.");
      return;
    }
    setWaitlistLoading(true);
    try {
      await api.post("/waitlist", { email, source: "membership_waitlist" });
      toast.success("You're on the list. We'll be in touch soon.");
      setEmail("");
    } catch (e) {
      console.error(e);
      toast.error("Something went sideways. Please try again.");
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <section
      data-testid="membership-gateway"
      id="membership"
      className="relative py-24 lg:py-32 bg-gradient-to-b from-brand-parchment to-brand-cream overflow-hidden"
    >
      {/* Brand watercolor flowers */}
      <img src="/assets/flower_rose.png" alt="" aria-hidden="true" className="absolute -top-4 -left-4 w-60 lg:w-72 opacity-90 animate-float-slow pointer-events-none" />
      <img src="/assets/flower_cosmos.png" alt="" aria-hidden="true" className="absolute -bottom-2 right-0 w-60 lg:w-80 opacity-90 animate-drift pointer-events-none" />

      {/* Garden flowers around the section */}
      <FlowerBlue className="absolute top-12 left-[3%] w-20 h-20 opacity-90 animate-float-slow" />
      <FlowerYellow className="absolute top-32 left-[12%] w-14 h-14 opacity-95 animate-drift" />
      <FlowerLavender className="absolute bottom-8 left-[4%] w-14 h-24 opacity-95" />
      <FlowerPink className="absolute bottom-20 left-[16%] w-16 h-16 opacity-90 animate-float-slow" style={{ animationDelay: "0.6s" }} />
      <FlowerRed className="absolute top-20 right-[3%] w-20 h-20 opacity-95 animate-drift" />
      <FlowerPink className="absolute bottom-32 right-[5%] w-14 h-14 opacity-90 animate-float-slow" />
      <FlowerYellow className="absolute bottom-10 right-[18%] w-12 h-12 opacity-95 animate-drift" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.35em] text-brand-gold">
            Secure Checkout & Membership
          </span>
          <h2 className="mt-3 font-serif text-4xl lg:text-5xl text-brand-moss leading-tight">
            Join Our <span className="italic">Circle of Intention</span>.
          </h2>
          <p className="mt-5 text-lg text-brand-moss/75 leading-relaxed">
            Become a member for $10/month — or simply add your name to the
            waitlist and receive gentle updates as new collections bloom.
          </p>
        </div>

        {/* Members receive a thank-you box like this — real photo hero */}
        <div className="mb-12 grid md:grid-cols-5 gap-6 items-center">
          <div className="md:col-span-3 relative rounded-3xl overflow-hidden shadow-xl aspect-[5/4] border-4 border-brand-cream">
            <img
              src="/assets/membership_photo.jpg"
              alt="A 414, LLC thank-you box with candle, Dream stone, and card"
              data-testid="membership-hero-photo"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-2">
            <p className="font-script text-3xl text-[#B7574E] leading-tight">
              Every member&apos;s box arrives like this.
            </p>
            <p className="mt-4 text-brand-moss/75 leading-relaxed">
              A hand-poured candle, a small keepsake, and a hand-signed
              thank-you card — packed with tissue and prayer, shipped from our
              studio to your door.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Membership column */}
          <div className="relative bg-brand-moss text-brand-cream rounded-3xl p-10 lg:p-12 overflow-hidden">
            <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-brand-gold/10 blur-2xl" />
            <div className="relative">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-brand-gold">
              <Crown className="w-4 h-4" /> Membership · $10 / month
            </span>
            <h3 className="mt-4 font-serif text-3xl lg:text-4xl leading-tight">
              A monthly invitation to slow down.
            </h3>

            <ul className="mt-8 space-y-3">
              {perks.map((p) => (
                <li key={p} className="flex gap-3 text-sm text-brand-cream/85">
                  <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 space-y-3">
              <input
                type="email"
                data-testid="membership-email-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-brand-cream/10 border border-brand-cream/20 placeholder:text-brand-cream/40 text-brand-cream rounded-full px-5 py-3 focus:outline-none focus:border-brand-gold transition-colors"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSubscribe}
                  disabled={loading}
                  data-testid="membership-subscribe-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-moss hover:bg-brand-gold-light px-6 py-3.5 rounded-full text-sm tracking-wide transition-colors disabled:opacity-60"
                >
                  {loading ? "Opening checkout…" : "Begin Membership ($10/mo)"}
                </button>
                <button
                  onClick={handleWaitlist}
                  disabled={waitlistLoading}
                  data-testid="membership-waitlist-btn"
                  className="flex-1 inline-flex items-center justify-center border border-brand-cream/30 text-brand-cream hover:bg-brand-cream/10 px-6 py-3.5 rounded-full text-sm tracking-wide transition-colors disabled:opacity-60"
                >
                  {waitlistLoading ? "Adding…" : "Join the Waitlist"}
                </button>
              </div>
              <p className="text-[11px] text-brand-cream/55 mt-2 flex items-center gap-1.5">
                <Lock className="w-3 h-3" />
                Secured by Stripe · cancel anytime
              </p>
            </div>
            </div>
          </div>

          {/* Scan-to-pay column */}
          <div className="relative bg-brand-cream border border-brand-moss/15 rounded-3xl p-10 lg:p-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-brand-gold">
              <Smartphone className="w-4 h-4" /> Scan-to-Pay
            </span>
            <h3 className="mt-4 font-serif text-3xl lg:text-4xl text-brand-moss leading-tight">
              Prefer Cash App?
            </h3>
            <p className="mt-3 text-brand-moss/70 text-base leading-relaxed">
              Open your wallet, point your camera, and check out instantly with
              absolute end-to-end encryption.
            </p>

            <div className="mt-8 max-w-xs mx-auto">
              <div
                data-testid="qr-cashapp"
                className="bg-brand-parchment rounded-2xl p-5 text-center border border-brand-moss/10"
              >
                <div className="aspect-square w-full bg-brand-cream rounded-xl flex items-center justify-center mb-3 overflow-hidden">
                  <img
                    src="/assets/cashapp_qr.png"
                    alt="Cash App pay QR — $414LLC"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="font-serif text-lg text-brand-moss">Cash App</p>
                <p className="text-xs text-brand-moss/60 mt-0.5">$414LLC</p>
              </div>
            </div>

            <div className="mt-7 flex items-center gap-2 text-xs text-brand-moss/60 border-t border-brand-moss/10 pt-5">
              <CreditCard className="w-3.5 h-3.5" />
              Card checkout available throughout the shop, secured by Stripe.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
